/**
 * API Client — HTTP wrapper with CSRF cookie + session-based auth.
 * Used by both Superadmin and Tenant portals.
 */

let _csrfRetried = false

function buildUrl(path, params, baseUrlOverride = null) {
  const baseUrl = baseUrlOverride || getBaseUrl()
  const url = new URL(`${baseUrl}${path}`, window.location.origin)

  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '') {
      continue
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== '') {
          url.searchParams.append(key, String(item))
        }
      }

      continue
    }

    url.searchParams.set(key, String(value))
  }

  return url.toString()
}

function getTenantSlugFromHostname() {
  const host = window.location.hostname.toLowerCase()

  if (!host.endsWith('.ayawin.test') && !host.endsWith('.ayawin.tn') && !host.endsWith('.aya-win.com')) {
    return null
  }

  const [subdomain] = host.split('.')

  if (!subdomain || ['app', 'api', 'admin', 'www', 'localhost'].includes(subdomain)) {
    return null
  }

  return subdomain
}

function getBaseUrl() {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  // In production (admin.aya-win.com or tenant.aya-win.com), use same domain
  // In development, use configured URL (could be localhost or .test domains)
  if (envUrl) {
    return envUrl
  }
  // Production: relative path (same domain)
  // Development: getBaseUrl() called by buildUrl which uses import.meta.env fallback
  return ''
}

function getCsrfFromCookie() {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function getTenantSlug() {
  if (!window.location.pathname.startsWith('/tenant')) {
    return null
  }

  try {
    return window.localStorage.getItem('ayawin_tenant_slug') || getTenantSlugFromHostname()
  } catch {
    return getTenantSlugFromHostname()
  }
}

async function request(path, options = {}) {
  const { params, responseType, ...fetchOptions } = options
  
  // Determine base URL based on current context
  let baseUrlOverride = null
  
  // Check if we're in a tenant context
  const tenantSlug = getTenantSlug()
  
  // For local dev with .env.local override, use the configured URL
  // Otherwise, requests go to the same domain (production or dev subdomain)
  const envUrl = import.meta.env.VITE_API_BASE_URL
  if (envUrl) {
    // Local dev with explicit .env.local configured
    baseUrlOverride = envUrl
  }
  // else: use buildUrl's default (empty = same domain)
  
  const url = buildUrl(path, params, baseUrlOverride)

  const isFormData = fetchOptions.body instanceof FormData
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'Accept': 'application/json',
    ...(fetchOptions.headers || {}),
  }

  if (tenantSlug) {
    headers['X-Tenant-Slug'] = tenantSlug
  }

  const csrf = getCsrfFromCookie()
  if (csrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes((fetchOptions.method || 'GET').toUpperCase())) {
    headers['X-XSRF-TOKEN'] = csrf
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  })

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { path } }))
    throw new ApiError('Unauthorized', 401, null)
  }

  if (response.status === 403) {
    let body = null
    try { body = await response.json() } catch { /* ignore */ }
    // Tenant suspension sends a structured 403 with tenant_status
    if (body?.tenant_status) {
      window.dispatchEvent(new CustomEvent('tenant:blocked', {
        detail: { status: body.tenant_status, message: body.message },
      }))
    }
    throw new ApiError(body?.message || 'Forbidden', 403, body)
  }

  if (response.status === 422) {
    const data = await response.json()
    throw new ApiError(data.message || 'Validation error', 422, data.errors || {})
  }

  if (response.status === 419 && !_csrfRetried) {
    _csrfRetried = true
    try {
      await fetchCsrfCookie()
      const result = await request(path, options)
      return result
    } finally {
      _csrfRetried = false
    }
  }

  if (response.status >= 500) {
    throw new ApiError('Server error', response.status, null)
  }

  if (!response.ok) {
    const text = await response.text()
    throw new ApiError(text || `Error ${response.status}`, response.status, null)
  }

  if (response.status === 204) return null

  if (responseType === 'blob') {
    return response.blob()
  }

  if (responseType === 'text') {
    return response.text()
  }

  // Soft overdue flag — server attaches header, no body change needed
  const tenantStatus = response.headers.get('X-Tenant-Status')
  if (tenantStatus === 'overdue') {
    window.dispatchEvent(new CustomEvent('tenant:overdue'))
  }

  const ct = response.headers.get('content-type') || ''
  return ct.includes('application/json') ? response.json() : null
}

export async function fetchCsrfCookie() {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 8000)
  try {
    // Use same context-aware routing as request() function
    const baseUrl = getBaseUrl()
    
    await fetch(`${baseUrl}/sanctum/csrf-cookie`, {
      credentials: 'include',
      signal: controller.signal,
    })
  } catch {
    // Non-fatal: missing CSRF cookie will trigger a 419 → retry cycle
  } finally {
    clearTimeout(timer)
  }
}

export class ApiError extends Error {
  constructor(message, status, errors) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors
  }
}

export const apiClient = {
  get: (path, options = {}) => request(path, options),
  post: (path, payload, options = {}) => request(path, { ...options, method: 'POST', body: JSON.stringify(payload) }),
  put: (path, payload, options = {}) => request(path, { ...options, method: 'PUT', body: JSON.stringify(payload) }),
  patch: (path, payload, options = {}) => request(path, { ...options, method: 'PATCH', body: JSON.stringify(payload) }),
  delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
  upload: (path, formData, options = {}) => request(path, { ...options, method: 'POST', body: formData }),
}
